import type { V3 } from '../engine/math';
import type { Camera } from '../engine/renderer';

/**
 * Looking round the porch with a mouse or fingers. One finger or the left button orbits, unless `tool.begin` takes
 * the gesture (sculpting, carving, moving a pumpkin). Two fingers pinch to zoom and drag to pan, and always cancel a
 * tool gesture so a stray second finger never leaves a half-made cut. Right button or shift pans; the wheel zooms.
 * The controls move `goal`; the view eases toward it each frame. `keep` scores a goal (0 is fine): a move that would
 * raise a score above 0 is refused, one axis at a time, so the view slides along the limit instead of sticking.
 */
export interface ToolHooks {
  begin(e: PointerEvent): boolean;
  move(e: PointerEvent): void;
  end(e: PointerEvent): void;
  cancel(): void;
  hover?(e: PointerEvent): void;
}

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

export class Orbit {
  goal: Camera;
  private pts = new Map<number, { x: number; y: number }>();
  private mode: 'none' | 'orbit' | 'pan' | 'tool' | 'pinch' = 'none';
  private last = { x: 0, y: 0, d: 0 };
  /** where a pinch began; it does nothing until the fingers have really moved, so a sloppy double tap with two
   * fingers overlapping for a moment never zooms */
  private pinch0: { x: number; y: number; d: number } | null = null;
  tool: ToolHooks | null = null;
  /** a wheel the app takes for itself (a trackpad pinch over a pumpkin): true, and the view doesn't zoom */
  onWheel: ((e: WheelEvent) => boolean) | null = null;
  /** a short press that didn't move: selects things */
  onTap: ((e: PointerEvent) => void) | null = null;
  private press = { x: 0, y: 0, t: 0 };
  bounds = { min: 0.45, max: 8 };
  /** how far up or down the view may go; the carve step opens it right round the pumpkin */
  pitch = { min: 0.02, max: 1.25 };
  yaw = { min: -Infinity, max: Infinity };
  /** how far a goal strays somewhere it shouldn't (the porch: the ends of the house in the picture); 0 is fine */
  keep: ((g: Camera) => number) | null = null;
  /** seconds left of a slow ease: the view moving itself (a pumpkin let go) drifts rather than jumps. Any touch ends it */
  glide = 0;

  constructor(private el: HTMLElement, private view: Camera) {
    this.goal = { ...view, target: [...view.target] as V3 };
    el.style.touchAction = 'none';
    el.addEventListener('pointerdown', e => this.down(e));
    el.addEventListener('pointermove', e => this.move(e));
    el.addEventListener('pointerup', e => this.up(e));
    el.addEventListener('pointercancel', e => this.up(e, true));
    // a finger whose lift never arrived would turn the next touch into a pinch
    el.addEventListener('lostpointercapture', e => this.up(e, true));
    el.addEventListener('wheel', e => { e.preventDefault(); this.glide = 0; if (!this.onWheel?.(e)) this.zoom(Math.exp(e.deltaY * 0.0012)); }, { passive: false });
    el.addEventListener('contextmenu', e => e.preventDefault());
  }

  private down(e: PointerEvent) {
    this.glide = 0;
    // the first finger down: nothing else can still be on the glass
    if (e.isPrimary && this.pts.size) { if (this.mode === 'tool') this.tool?.cancel(); this.pts.clear(); this.mode = 'none'; }
    try { this.el.setPointerCapture(e.pointerId); } catch { /* already lifted */ }
    this.pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (this.pts.size === 2) {
      if (this.mode === 'tool') this.tool?.cancel();
      this.mode = 'pinch';
      const [a, b] = [...this.pts.values()];
      this.last = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, d: Math.hypot(a.x - b.x, a.y - b.y) };
      this.pinch0 = { ...this.last };
      return;
    }
    if (this.pts.size > 2) return;
    this.last = { x: e.clientX, y: e.clientY, d: 0 };
    this.press = { x: e.clientX, y: e.clientY, t: performance.now() };
    if (e.button === 2 || e.shiftKey) { this.mode = 'pan'; return; }
    if (e.button === 0 && this.tool?.begin(e)) { this.mode = 'tool'; return; }
    this.mode = 'orbit';
  }

  private move(e: PointerEvent) {
    if (!this.pts.has(e.pointerId)) { this.tool?.hover?.(e); return; }
    this.pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const h = this.el.clientHeight || 1;
    if (this.mode === 'tool') { this.tool?.move(e); return; }
    if (this.mode === 'pinch') {
      const [a, b] = [...this.pts.values()];
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2, d = Math.hypot(a.x - b.x, a.y - b.y), p0 = this.pinch0;
      if (p0 && Math.abs(d - p0.d) < 24 && Math.hypot(mx - p0.x, my - p0.y) < 24) return;
      if (p0) { this.pinch0 = null; this.last = { x: mx, y: my, d }; return; }
      if (this.last.d > 0 && d > 0) this.zoom(this.last.d / d);
      this.pan(mx - this.last.x, my - this.last.y, h);
      this.last = { x: mx, y: my, d };
      return;
    }
    const dx = e.clientX - this.last.x, dy = e.clientY - this.last.y;
    this.last = { x: e.clientX, y: e.clientY, d: 0 };
    if (this.mode === 'orbit') {
      this.nudge(g => { g.yaw = clamp(g.yaw - dx / h * 3.2, this.yaw.min, this.yaw.max); });
      this.nudge(g => { g.pitch = clamp(g.pitch + dy / h * 2.4, this.pitch.min, this.pitch.max); });
    } else if (this.mode === 'pan') this.pan(dx, dy, h);
  }

  private up(e: PointerEvent, cancelled = false) {
    if (!this.pts.has(e.pointerId)) return;
    this.pts.delete(e.pointerId);
    if (this.mode === 'tool') { if (cancelled) this.tool?.cancel(); else this.tool?.end(e); }
    else if (this.mode === 'orbit' && !cancelled && performance.now() - this.press.t < 300
      && Math.hypot(e.clientX - this.press.x, e.clientY - this.press.y) < 8) this.onTap?.(e);
    if (this.pts.size === 1 && this.mode === 'pinch') {
      // one finger left after a pinch: carry on orbiting from where it is, never start a tool mid-gesture
      const [p] = [...this.pts.values()];
      this.last = { x: p.x, y: p.y, d: 0 };
      this.mode = 'orbit';
      return;
    }
    if (!this.pts.size) this.mode = 'none';
  }

  get busy() { return this.mode !== 'none'; }

  zoom(k: number) {
    const dist = (g: Camera) => { g.dist = clamp(g.dist * k, this.bounds.min, this.bounds.max); };
    // backing off at the edge of the turn swings the view back toward the middle instead of refusing
    const mid = Number.isFinite(this.yaw.min + this.yaw.max) ? (this.yaw.min + this.yaw.max) / 2 : this.goal.yaw;
    const swing = (g: Camera) => { g.yaw += (mid - g.yaw) * 0.2; };
    if (!this.nudge(dist) && !this.nudge(g => { dist(g); swing(g); })) this.nudge(swing);
  }

  private pan(dx: number, dy: number, h: number) {
    const s = this.goal.dist / h * 1.1, cy = Math.cos(this.goal.yaw), sy = Math.sin(this.goal.yaw);
    this.nudge(g => {
      g.target[0] = clamp(g.target[0] - dx * cy * s, -4, 4);
      g.target[2] = clamp(g.target[2] + dx * sy * s, -2.4, 5);
    });
    // up and down the screen: along the camera's own up, so with the view tipped down it slides over the boards, still
    // inside the same box and whatever `keep` allows. At the floor or the ceiling the whole step stops, so the target
    // never carries on sliding over the boards
    const sp = Math.sin(this.goal.pitch), cp = Math.cos(this.goal.pitch);
    const y0 = this.goal.target[1], m = cp > 1e-3 ? (clamp(y0 + dy * s * cp, 0.05, 2.2) - y0) / (s * cp) : dy;
    this.nudge(g => {
      g.target[0] = clamp(g.target[0] - m * s * sp * sy, -4, 4);
      g.target[1] = clamp(g.target[1] + m * s * cp, 0.05, 2.2);
      g.target[2] = clamp(g.target[2] - m * s * sp * cy, -2.4, 5);
    });
  }

  /** apply a change to the goal unless `keep` says it goes somewhere it shouldn't (or further in, if it's already there) */
  nudge(change: (g: Camera) => void): boolean {
    const next = { ...this.goal, target: [...this.goal.target] as V3 };
    change(next);
    if (this.keep) {
      const k = this.keep(next);
      if (k > 0 && k >= this.keep(this.goal)) return false;
    }
    Object.assign(this.goal, next);
    return true;
  }

  /** bring the view's yaw within half a turn of the goal's, so a step change never spins it round the long way */
  unwind() {
    const d = this.view.yaw - this.goal.yaw;
    this.view.yaw -= Math.round(d / (2 * Math.PI)) * 2 * Math.PI;
  }

  /** move the goal to look at a point from a distance, keeping the angle */
  focus(at: V3, dist: number, lens = 0, lensX = 0) {
    this.goal.target = [...at] as V3;
    this.goal.lens = lens;
    this.goal.lensX = lensX;
    this.goal.dist = Math.max(this.bounds.min, Math.min(this.bounds.max, dist));
  }

  /** ease the view toward the goal; `dt` in seconds */
  step(dt: number) {
    const k = 1 - Math.exp(-dt * (this.glide > 0 ? 2.2 : 10)), v = this.view, g = this.goal;
    this.glide = Math.max(0, this.glide - dt);
    v.yaw += (g.yaw - v.yaw) * k;
    v.pitch += (g.pitch - v.pitch) * k;
    v.dist += (g.dist - v.dist) * k;
    v.lens = (v.lens ?? 0) + ((g.lens ?? 0) - (v.lens ?? 0)) * k;
    v.lensX = (v.lensX ?? 0) + ((g.lensX ?? 0) - (v.lensX ?? 0)) * k;
    v.fov = (v.fov ?? 0.95) + ((g.fov ?? 0.95) - (v.fov ?? 0.95)) * k;
    v.roll = (v.roll ?? 0) + ((g.roll ?? 0) - (v.roll ?? 0)) * k;
    for (let i = 0; i < 3; i++) v.target[i] += (g.target[i] - v.target[i]) * k;
  }
}
